import { useAction } from '@reatom/npm-react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { openSupportChatAction } from '@root/features/support'
import { ApiError } from '@root/shared/api'
import {
  FastifyErrorCode,
  PremiumErrorCode,
} from '@root/shared/api/api-error-code'
import { CheckCircleIcon, CrossCircleIcon } from '@root/shared/ui-kit'
import { GlassDialog } from '@root/shared/ui-kit/glass-dialog'

import type { UpdatePremiumCurrencyRequestError } from '@root/features/premium/update-currency'
import type { Error } from '@root/shared'

type Props = {
  requestState: AsyncStateN.AsyncStateN<
    Error.UnknownError | UpdatePremiumCurrencyRequestError,
    unknown
  >
  onClose?: () => void
  onReset?: () => void
}

export const UpdateLogoProgress: React.FC<Props> = ({
  requestState,
  onClose,
  onReset,
}) => {
  const { t } = useTranslation()
  const openSupportChart = useAction(openSupportChatAction)

  const handleSupport = () => {
    openSupportChart()
  }

  if (AsyncStateN.isNotInitiated(requestState)) {
    return
  }

  const render = () => {
    if (AsyncStateN.isSuccess(requestState)) {
      return (
        <>
          <IconSuccess size={40} color="var(--success-9)" />
          <Title>
            <Trans
              i18nKey="premium.update-logo.success"
              components={[<span key={0} />]}
            />
          </Title>
          <Footer>
            <Button onClick={onClose}>{t('common.continue')}</Button>
          </Footer>
        </>
      )
    }

    if (AsyncStateN.isError(requestState)) {
      const error = requestState.error
      const message = (() => {
        if (
          ApiError.isOfCodes([
            PremiumErrorCode.ImageContentNotSafe,
            PremiumErrorCode.ImageSafetyCheckFailed,
          ])(error)
        ) {
          return t('premium.update-logo.image-safety-heck-failed')
        }
        if (ApiError.isOfCode(FastifyErrorCode.ContentBodyTooLarge)(error)) {
          return t('common.file-size-too-big')
        }

        return t('common.something_went_wrong_try_again')
      })()

      return (
        <>
          <IconError size={40} />
          <Title>{message}</Title>
          <Footer>
            <Button onClick={onReset}>
              {t('premium.update-logo.error.retry')}
            </Button>
            <Button onClick={handleSupport}>
              {t('premium.update-logo.error.support')}
            </Button>
          </Footer>
        </>
      )
    }

    return (
      <>
        <CircularLoader size={36} />
        <Title>
          <Trans
            i18nKey="premium.update-logo.processing"
            components={[<span key={0} />]}
          />
        </Title>
        <Footer />
      </>
    )
  }

  return (
    <GlassDialog intent={requestState} open>
      <Content>{render()}</Content>
    </GlassDialog>
  )
}

const IconSuccess = styled(CheckCircleIcon)``

const IconError = styled(CrossCircleIcon)`
  color: var(--red-9);
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  padding: 0 52px;

  margin-top: 16px;

  span {
    color: var(--text-9);

    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    display: block;
  }
`

const Content = styled.div`
  padding-top: 30px;

  display: grid;
  place-content: center;
  place-items: center;
`

const Footer = styled.footer`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;

  padding-top: 8px;
  height: 48px;
  gap: 4px;
`

const Button = styled.button`
  background: var(--backgroundA-3);
  color: var(--text-11);

  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  border: 0;
  border-radius: 64px;
  height: 24px;
  padding: 0px 12px;
  flex-shrink: 0;

  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
`
